/**
 * Internal identifiers and translatable UI text for QuickPoint.
 *
 * `strings` — non-translatable constants (event names, CSS classes, message
 *              identifiers, etc.).  Uses `as const` so literal types are
 *              preserved for discriminated unions.
 *
 * `text`    — user-facing translatable strings.  Each sub-object uses
 *             JavaScript getters that check the current locale and return the
 *             correct translation.  Adding a new language only requires
 *             creating a translation file and registering it in
 *             `translations/index.ts`.
 *
 * @module strings
 */
import { keywords } from "./keywords";
import { getLocale } from "./locale.js";
import { translations } from "./translations/index.js";
import type { TranslationSet } from "./translations/index.js";

// ── Localization helpers ─────────────────────────────────────────────

type StringRecord = Record<string, string>;

/**
 * Creates an object whose properties are getters.
 * Each getter returns the translated string (if a translation exists for the
 * current locale) or the English fallback.
 */
function localized<T extends StringRecord>(
	section: keyof TranslationSet,
	en: T,
): T {
	const obj: Record<string, unknown> = {};
	for (const key of Object.keys(en)) {
		Object.defineProperty(obj, key, {
			get() {
				const locale = getLocale();
				if (locale !== "en") {
					const lang = translations[locale];
					const translated = (lang?.[section] as StringRecord | undefined)?.[key];
					if (translated !== undefined) return translated;
				}
				return en[key];
			},
			enumerable: true,
		});
	}
	return obj as T;
}

/**
 * Like localized() but handles values that can be strings OR functions.
 */
function localizedMixed<T extends Record<string, string | ((...args: any[]) => string)>>(
	section: keyof TranslationSet,
	en: T,
): T {
	const obj: Record<string, unknown> = {};
	for (const key of Object.keys(en)) {
		Object.defineProperty(obj, key, {
			get() {
				const locale = getLocale();
				if (locale !== "en") {
					const lang = translations[locale];
					const translated = (lang?.[section] as Record<string, unknown> | undefined)?.[key];
					if (translated !== undefined) return translated;
				}
				return en[key];
			},
			enumerable: true,
		});
	}
	return obj as T;
}

/**
 * Localized helper for nested sections like lint.actions / lint.messages.
 */
function localizedNested<T extends StringRecord>(
	section: keyof TranslationSet,
	nestedKey: string,
	en: T,
): T {
	const obj: Record<string, unknown> = {};
	for (const key of Object.keys(en)) {
		Object.defineProperty(obj, key, {
			get() {
				const locale = getLocale();
				if (locale !== "en") {
					const lang = translations[locale];
					const nested = (lang?.[section] as Record<string, unknown> | undefined)?.[nestedKey];
					const translated = (nested as StringRecord | undefined)?.[key];
					if (translated !== undefined) return translated;
				}
				return en[key];
			},
			enumerable: true,
		});
	}
	return obj as T;
}

function localizedNestedMixed<T extends Record<string, string | ((...args: any[]) => string)>>(
	section: keyof TranslationSet,
	nestedKey: string,
	en: T,
): T {
	const obj: Record<string, unknown> = {};
	for (const key of Object.keys(en)) {
		Object.defineProperty(obj, key, {
			get() {
				const locale = getLocale();
				if (locale !== "en") {
					const lang = translations[locale];
					const nested = (lang?.[section] as Record<string, unknown> | undefined)?.[nestedKey];
					const translated = (nested as Record<string, unknown> | undefined)?.[key];
					if (translated !== undefined) return translated;
				}
				return en[key];
			},
			enumerable: true,
		});
	}
	return obj as T;
}

// ── English strings (used as source of truth for translatable text) ──

const controlsEn = {
	ai: "Use AI",
	close: "Exit",
	discuss: "Discuss",
	file: "File",
	files: "Files",
	folder: "Folder",
	logout: "Logout",
	mobilePreview: "Preview",
	more: "More",
	moreOptions: "More options",
	newStack: "New",
	preview: "Preview",
	present: "Present",
	publish: "Publish",
	qr: "qr",
	read: "Read",
	rename: "Rename presentation",
	save: "Save",
	saved: "Saved",
	saving: "Saving",
	share: "Share",
	stacks: "Presentations",
	tour: "Tour",
	trash: "Trash",
	trashImage: "Trash image",
	unpreview: "Un-preview",
	unpublish: "Un-Pub",
	unread: "Un-read",
};

const textControlsEn = {
	bold: "bold",
	codeBlock: "new code or math",
	col: "new col",
	endGrid: "finish the grid",
	insertFilter: "insert a filter",
	fold: "fold code",
	italic: "italic",
	page: "new page",
	row: "new row",
	underline: "underline",
	undo: "undo",
	mdToJadle: "markdown to jadle",
};

const errorsEn = {
	emailLinkExpired: "Verify email link expired",
	invalidCredentials: "Invalid credentials",
	noStack: "A new presentation.",
	notFound: "No presentations live at this address. Sorry",
	chartNoFile: "No data file found for this chart. Pick one from your files.",
	chartNoData: "This file has no data we can chart. It needs a header row and at least one column of numbers.",
	chartDefaultName: "Chart of",
	publishFailed: "Publishing didn't go through. Try again in a moment.",
};

const lintActionsEn = {
	remove: "Remove",
};

const lintMessagesEn = {
	closeGridWithoutOpen: "You tried to end a grid with '-->', but never started one. Start one with '---' first." as string,
	unclosedGridBeforePage: "This grid wasn't finished before the page ended. Add '-->' where you want it to end." as string,
	unclosedCodeBeforePage: "This code block wasn't finished before the page ended. Add '```' where you want it to end." as string,
	unclosedStickyHeaderBeforePage: "This sticky header wasn't finished before the page ended. Add another '^^^' where you want it to end." as string,
	unclosedStickyFooterBeforePage: "This sticky footer wasn't finished before the page ended. Add another 'vvv' where you want it to end." as string,
	unclosedGridAtDocEnd: "The document ends while this grid is still open. Add '-->' to close it where you want it to end." as string,
	unclosedCodeAtDocEnd: "The document ends while this code block is still open. Add '```' to close it where you want it to end." as string,
	unclosedStickyHeaderAtDocEnd: "The document ends while this sticky header is still open. Add another '^^^' to close it." as string,
	unclosedStickyFooterAtDocEnd: "The document ends while this sticky footer is still open. Add another 'vvv' to close it." as string,
	multiInstructionOnLine: "Only use one keyword per line. Move extra keywords or modifiers to their own lines." as string,
	modifierNeedsKeyword: "This modifier needs a keyword above it (like title: or image:). Indent it under the text you want to change." as string,
	missingValue: ((token: string) => {
		const t = token as unknown as string;
		const isKeyword = (keywords as readonly string[]).includes(t);
		if (isKeyword) {
			if (t === "image:") return `"${t}" needs an image. Pick one from your files.`;
			if (t === "background:") return `"${t}" needs a color name or an image. Add it on the same line.`;
			if (t === "video:") return `"${t}" needs a video link (YouTube or Vimeo). Paste the link after it.`;
			if (t === "audio:") return `"${t}" needs an audio file. Pick one from your files.`;
			if (t === "map:") return `"${t}" needs a Google Maps share link. Paste it after the keyword.`;
			if (t === "chart:") return `"${t}" needs a data file (CSV or JSON). Pick one from your files.`;
			if (t === "filter:") return `"${t}" needs a filter name. Choose one from your filters.`;
			if (t === "moveBy:") return `"${t}" needs a choice: "tap" or "scroll".`;
			if (t === "autoplay:") return `"${t}" needs a number of seconds. Example: autoplay: 5`;
			return `"${t}" needs text. Type it on the same line.`;
		}
		return `"${t}" needs a value. Add it on the same line.`;
	}) as (token: string) => string,
	modifierNotAllowed: ((modifier: string, keyword: string) =>
		`The modifier "${modifier}" can't be used with "${keyword}".`) as (modifier: string, keyword: string) => string,
	incorrectIndentation: "Incorrect indentation. Please use exactly two spaces." as string,
};

const fileUploaderEn = {
	title: "Drop New Files Here",
	uploadTitle:
		"Enter a name for your file. We also require a description for readers with accessibility issues.",
	tabsLabel: "File types",
	tabAi: "AI",
	tabAudio: "Audio",
	tabImages: "Images",
	tabData: "Data",
	searchImagesOpen: "Search for Images",
	searchImagesBack: "Back to files",
	searchImagesLabel: "Search free-to-use images",
	searchImagesPlaceholder: "Search photos and diagrams…",
	searchImagesButton: "Search",
	searchImagesSearching: "Searching…",
	searchImagesNoResults: "No images found. Try different words.",
	searchImagesAdd: ((title: string) => `Add “${title}” to your files`) as (
		title: string,
	) => string,
	searchImagesAdding: "Adding…",
	searchImagesAdded: "Added to your files",
	searchImagesAddedAs: ((name: string) =>
		`Added to your files as “${name}”`) as (name: string) => string,
	searchImagesError: "Something went wrong adding that image. Try again.",
	searchImagesIntro:
		"Search millions of free-to-use photos and diagrams. Click one to add it to your files.",
	browse: "Drop Files / Browse Files",
	aiPrompt: "Image prompt",
	aiPlaceholder: "Enter a detailed prompt to generate an image...",
	generate: "Generate",
	generating: "Generating...",
	clear: "Clear",
	zoomIn: "Zoom image",
	zoomOut: "Close zoomed image",
	fileMissing: "File Missing",
	nameLabel: "File nickname",
	namePlaceholder: "Enter Meaningful Nickname",
	altLabel: "Accessibility description",
	altPlaceholder: ((fileType: string) =>
		`Describe the ${fileType} for accessibility`) as (fileType: string) => string,
	nameRequired: "This field is required",
	altRequired: "Required for accessibility.",
	upload: "Upload",
	excelConvertError:
		"We could not read this spreadsheet. Please save it as .xlsx or CSV and try again.",
};

const imagePickerEn = {
	title: "Choose images for your presentation",
	searching: "searching…",
	noResults: "No public domain images found",
	generateWithAi: "Generate with AI",
	skip: "Skip",
	useSelected: "Use selected images",
	selectEach: "Select an option for each image.",
	skipAll: "Skip all images",
	selected: "Selected",
	stepOf: ((n: number, total: number) => `Image ${n} of ${total}`) as (
		n: number,
		total: number,
	) => string,
	next: "Next",
	back: "Back",
	previewHint: "Select an option below to preview it.",
	editPromptLabel: "Describe the image to generate — add as much detail as you like",
	skipPreviewNote: "This slide will have no image.",
	aiGenerated: "AI image",
	generateFailed: "Image generation failed. Please try again.",
	generateToChoose: "Generate, then select an image to continue.",
	unusedGenerated: ((n: number) =>
		n === 1
			? "You generated 1 AI image you didn't use. Keep it in your files?"
			: `You generated ${n} AI images you didn't use. Keep them in your files?`) as (
		n: number,
	) => string,
	keepUnused: "Keep them",
	discardUnused: "Discard unused images",
};

const uiEn = {
	thinking: "THINKING",
	saving: "Saving",
	skipToEditor: "Skip to editor",
	editorLabel: "Presentation structure editor",
	done: "Done",
	create: "Create",
	ok: "OK",
	cancel: "Cancel",
	chooseTour: "Choose Your Tour",
	closeTourSelection: "Close tour selection",
	tourOptions: "Tour options",
	networkError: "There is a problem with the internet. Try refreshing?",
	mainNavigation: "Main navigation",
	storyGroup: "Presentation",
	sharingGroup: "Sharing",
	accountGroup: "Help and account",
	signIn: "Sign In",
	signInToSave: "Sign in to save your work",
	generatingAssets: "Generating Assets",
	publishBeforeShare:
		"The presentation must be published before it can be shared — but you can still export it as a PDF.",
	deleteConfirm: ((name: string) =>
		`Are you sure you want to DELETE "${name}"?? This cannot be undone`) as (name: string) => string,
};

const previewEn = {
	present: "\u25B6 Present",
	presentLabel: "Present fullscreen",
	previewControls: "Preview controls",
	desktopPreview: "Desktop preview",
	mobilePreview: "Mobile preview",
	fullSizePreview: "Open full size preview",
	fullSizePreviewLabel: "Open full size preview in new window",
	exportPdf: "Export PDF",
	exportPdfLabel: "Export presentation as PDF",
	exportPdfLoading: "Generating PDF...",
	exportPdfError: "PDF export failed",
	goToPage: ((page: number) => `Go to page ${page}`) as (page: number) => string,
	previewOfPage: ((page: number) => `Preview of page ${page}`) as (page: number) => string,
};

// reader-facing viewer strings (resume-reading prompts)
const readerEn = {
	resumeExitStory: ((exit: string) =>
		`Last time you ended up at ${exit}. Go there?`) as (exit: string) => string,
	resumeLastPage: "Go to the last page you were reading?",
	resumeGoToStart: "Go to the start of the story?",
	resumeYes: "Yes",
	resumeNo: "No",
	// attribution on free-tier published stories — the viral loop
	madeWith: "Made with QuickPoint",
	madeWithCta: "create yours free",
	// caption of the chart's hidden data table (the summary precedes it)
	chartDataTable: "Chart data",
	// the disclosure button revealing that table
	chartDataToggle: "See chart data",
	// keyboard shortcuts overlay (opened with ?)
	shortcutsTitle: "Keyboard shortcuts",
	shortcutsClose: "Close",
	shortcutsNext: "Next slide",
	shortcutsNextKeys: "→ · Space · Page Down",
	shortcutsPrevious: "Previous slide",
	shortcutsPreviousKeys: "← · Shift+Space · Page Up",
	shortcutsFirstLast: "First / last slide",
	shortcutsFirstLastKeys: "Home / End",
	shortcutsSettings: "Accessibility settings",
	shortcutsSettingsKeys: "button, bottom right",
	shortcutsHelp: "Show this help",
	shortcutsHelpKeys: "?",
	// audio player seek slider accessible name
	audioSeek: "Seek within the audio",
};

// "tkns" is a brand word — identical in every locale
const tknsEn = {
	title: "TKNS",
	tagline: "TKNS pay for the AI, priced at what it costs us — a chat is about 1 TKN, an image 34.",
	yourBalance: "Your balance",
	balance: ((n: number) => `${n.toLocaleString("en-GB")} TKNS`) as (n: number) => string,
	packsTitle: "Top-up packs",
	packsNote: "Purchased TKNS never expire.",
	buy: "Buy",
	perMonth: ((price: string) => `${price}/month`) as (price: string) => string,
	subscribe: "Subscribe",
	subsSoon: "Subscriptions are opening shortly — your monthly TKNS will arrive automatically.",
	subscribed: "Welcome to Plus — your monthly TKNS are in your balance.",
	cancelSub: "Cancel subscription",
	cancelSubConfirm: "Cancel your Plus subscription? You'll be back on the free plan right away — TKNS already in your balance stay spendable.",
	cancelSubDone: "Your Plus subscription is cancelled.",
	keepSub: "Keep Plus",
	freeIncludes: ((tkns: string, presentations: number, mb: number) =>
		`Free includes ${tkns} TKNS a month, ${presentations} presentations, ${mb} MB, and one published story at a time.`) as (tkns: string, presentations: number, mb: number) => string,
	benefitPresentations: "Unlimited presentations and publishing",
	benefitStorage: ((gb: number) => `${gb} GB storage`) as (gb: number) => string,
	benefitTkns: ((n: string) => `${n} TKNS every month`) as (n: string) => string,
	benefitNoBadge: "No “Made with QuickPoint” badge on your published stories",
	meterToday: "AI left today",
	meterMonth: "AI left this month",
	meterLeftOf: ((left: string, total: string) => `${left} of ${total} TKNS`) as (left: string, total: string) => string,
	freeTitle: "Free",
	yourPlan: "Your plan",
	benefitFreePresentations: ((n: number) => `${n} presentations`) as (n: number) => string,
	benefitFreeStorage: ((mb: number) => `${mb} MB storage`) as (mb: number) => string,
	benefitFreePublished: "One published story at a time, with the “Made with QuickPoint” badge",
	benefitDaily: ((n: string) => `Up to ${n} TKNS of AI a day`) as (n: string) => string,
	buying: "Opening checkout…",
	waitingForPayment: "Waiting for payment confirmation…",
	purchased: ((n: number) => `${n.toLocaleString("en-GB")} TKNS added to your balance.`) as (n: number) => string,
	purchaseTimeout: "Payment not confirmed yet — your TKNS will appear here once it completes.",
	insufficient: "You're out of TKNS. Top up to keep using the AI.",
	dailyCap: "You've used today's AI allowance — it resets tomorrow. Plus has a much higher daily limit.",
	freeLimitPresentations: "The free plan holds 3 presentations. Upgrade to Plus for unlimited presentations.",
	freeLimitPublished: "The free plan publishes one story at a time — unpublish the other one first, or go Plus for unlimited publishing.",
	freeLimitStorage: "Your storage is full. Delete some files, or upgrade to Plus for 2 GB.",
	topUp: "Top up",
	signInFirst: "Sign in to see your TKNS and top up.",
	loadError: "Couldn't load TKNS right now. Try again in a moment.",
	done: "Done",
};

const chatEn = {
	rateLimited: "You're sending AI requests too quickly. Please wait a moment and try again.",
	thinking: "Thinking...",
	you: "You",
	ai: "AI",
	human: "Greg",
	humanHelpHint: "If you ever need help from a human, just ask \u2014 Greg reads these.",
	humanReplied: "Greg replied in the chat",
	messageSent: "Message sent",
	aiResponding: "AI is responding...",
	generationCancelled: "Generation cancelled",
	chatLabel: "AI chat conversation",
	createPrompt: "Create the presentation based on our discussion.",
	tabChat: "Chat",
	tabEditElement: "Edit Element",
	tabStructure: "Structure",
	editingMode: "Editing mode",
};

const tourEn = {
	aiPowered: "Quick Start",
	aiPoweredLabel: "Quick Start tour, approximately 3 minutes",
	aiPoweredDuration: "~3 minutes",
	aiPoweredDescription:
		"Learn the basics and create your first presentation",
	startTour: "Start Tour",
	moreTours: "More tours",
	fewerTours: "Fewer tours",
	jumpToSection: "Or jump straight into a Full Tour section:",
	jumpToSectionLabel: ((section: string) =>
		`Start the Full Tour at the ${section} section`) as (section: string) => string,
	accessibility: "Accessibility",
	accessibilityLabel: "Accessibility tour, approximately 4 minutes",
	accessibilityDuration: "~4 minutes",
	accessibilityDescription:
		"Discover keyboard navigation, screen reader support, and inclusive design features",
	fullTour: "Full Tour",
	fullTourLabel: "Full Tour, approximately 10 minutes",
	fullTourDuration: "~10 minutes",
	fullTourDescription:
		"Learn all features with a complete walkthrough",
};

const shareEn = {
	share: "Share",
	startAtPage: "Start link at the current page?",
	linkStartsAtPage: ((page: number) => `The link starts at page ${page}`) as (page: number) => string,
	readModeOn: "Read Mode is on",
	shareInReadMode: "Share in Read mode?",
	downloadQrAs: "Download QR code as:",
	copyLink: "Copy link",
	linkCopied: "Link copied",
	downloadQr: "Download QR",
	qrCodeLabel: "QR code linking to this presentation",
	linkSection: "Link",
	downloadsSection: "Downloads",
	linkUpdated: "Link updated",
	pdfHint: "Available before publishing.",
};

const tourStepsEn = {
	// welcome
	welcomeIntro: "Welcome to QuickPoint.",
	welcomeDescription: "Create presentations by simply describing what you want. Built for complete accessibility, with no design skills needed.",
	chatTabIntro: "This is the Chat tab. Describe what you want and AI will create it for you.",
	chatInputHint: "Just type what you want here. For example: 'Create a presentation about climate change' or 'Make slides for my product launch'.",
	chatPasteNotes: "Tip: You can paste your class notes or presentation notes directly into the chat. Tell the AI what to do with them — for example: 'Turn these notes into a 5-slide presentation'.",

	// aiDemo
	demoPrompt: "Create a presentation about renewable energy with sections on solar and wind power",
	demoTyping: "Let's see how it works. Watch as we type a request...",
	demoAiPlans: "The AI reads your request and suggests a plan. It explains what it will create and why.",
	demoCreateButton: "When you're happy with the plan, click Create and the AI builds your presentation. You can keep chatting to refine the plan first.",
	demoPresentationAppears: "Your presentation appears instantly. The AI created a title slide, content slides, and applied a professional design.",
	demoNavigate: "Navigate through the slides to see what the AI created.",
	mobileDeckReady: "Your presentation is ready. On a phone, the preview lives in its own browser tab — let's open it.",
	mobilePreviewTap: "Tap Preview. Your presentation opens in a new tab: flip through the pages, tap any text or image, then swipe back to this tab.",
	mobilePreviewWaiting: "This step finishes by itself when you tap an element over there and come back.",
	mobileTappedElement: "See how that works? Tap an element in the preview and it loads here, ready to edit. Let's edit the title on page 2 together.",
	mobileTwoTabs: "Your preview keeps its own tab — swipe between the two as you work. It always shows your latest changes, and rotating your phone shows it bigger.",
	demoSlideDesign: "Each slide is automatically designed with images, text, and layout.",
	demoContinueConversation: "You can continue the conversation to make changes:",
	demoExampleHydro: "\"Add a slide about hydropower\"",
	demoExampleRewrite: "\"Rewrite the wind energy section\"",
	demoExampleCaptions: "\"Add captions to all the images\"",
	demoMockRationale: "This presentation follows the principles of high-density communication and graphical excellence — giving the viewer the greatest number of ideas in the shortest time with the least ink.\n\n1. **Narrative Arc**: A title slide frames the topic, two evidence slides present solar and wind energy, and a closing slide provides a forward-looking message. Clear beginning, middle, and end.\n2. **Layout Logic**:\n   - **Slide 1** uses a **title + subtitle** cover to establish context immediately.\n   - **Slide 2** (Solar Power) uses a **Narrative Split** — image with caption paired with body text to integrate evidence and explanation.\n   - **Slide 3** (Wind Energy) uses an **image + caption** with concise body text highlighting key benefits.\n   - **Slide 4** closes with a **Big Statement** — title + subtitle, letting typography carry the impact.\n3. **Constraints**: Every slide fits within a single viewport with no scrolling. Body text is kept under 50 words per slide and left-aligned for readability and accessibility." as string,

	// editElement
	quickEdits: "Quick Edits",
	editElementIntro: "The Edit Element tab lets you make quick changes to any element.",
	editElementClick: "Click on any text or image in the preview to edit it directly.",
	editElementTry: "Let's try it. We've selected the title from slide 2.",
	editElementInlineEditor: "The inline editor opens with just that element's code.",
	editElementWatch: "Watch as we change \"Solar Power\" to something new.",
	editElementRealtime: "The preview updates in real time.",
	editElementReflected: "The change is reflected instantly in the preview. No save needed.",
	editElementPerfectFor: "This is perfect for:",
	editElementFixTypos: "- Fixing typos",
	editElementAdjustWording: "- Adjusting wording",
	editElementChangeElements: "- Changing individual elements",
	editElementNoNeedToFind: "No need to find it in the full code.",
	editElementReplacement: "The Power of the Sun",

	// revealStructure
	secretPower: "The Secret Power",
	structureTabIntro: "The Structure tab reveals the full power of QuickPoint.",
	structureBuiltWithText: "This is where presentations are actually built - with simple text.",
	structureLabels: "Every presentation is just text with simple labels like title: and image:",
	structureAiWrites: "The AI writes this for you, but you can edit it directly too.",
	structureTypeSlide: "Type === to create a new slide.",
	structureTypeTitle: "Type title: followed by your text.",
	structureTypeImage: "Type image: to add an image.",
	structureThatsAll: "That's really all there is to it.",
	structureHints: "Helpful prompts appear here as you type, showing you what options are available.",
	structureInstantPreview: "Changes appear instantly in the preview. No 'save' or 'refresh' needed.",
	designTitle: "Good design, built on science",
	designBody1: "QuickPoint's design is grounded in information design theory — maximising clarity, minimising clutter, and respecting your audience's attention. Every layout is built to show the most ideas in the least space, with clean typography, purposeful imagery, and no filler.",
	designBody2: "The AI applies these principles for you — organising your ideas into well-structured slides so you can focus on what you want to say. It's a time-saver, not a replacement for your thinking.",
	whyText: "Why text-based slides?",
	whyTextResearch: "Research shows standard bullet-point templates hurt understanding and retention. QuickPoint is different:",
	whyFast: "Fast — Type what you mean, skip clicking endless menus",
	whyPrecise: "Precise — Full layout and content control options with simple text commands",
	whyAccessible: "Accessible — Fully inclusive design for all to build, present, and view",
	whyPowerful: "Powerful — Handles formatting for you, move beyond drag and drop",

	// whatNext
	yourWorkflow: "Your Workflow",
	startWithChat: "Start with Chat - describe what you want, let AI do the heavy lifting.",
	useEditElement: "Use Edit Element - for quick fixes and tweaks to individual items.",
	masterStructure: "Master Structure - for full control and advanced layouts.",
	storiesSaved: "All your presentations are saved here. Click to see your work.",
	clickPublish: "Click Publish to make your presentation shareable.",
	shareWithLink: "Then share it with a link or QR code. Works on any device.",
	clickTour: "Click Tour anytime to revisit this guide. Browse the Accessibility Tour to see the complete accessibility features, or take the Full Tour for a walkthrough of everything.",
	readyToGo: "You're ready to go!",
	startByTyping: "Start by typing what you want to create in the Chat.",

	// ── Shared tour steps (accessibility + original) ──────────────────
	// intro
	fullWelcome: "Welcome to QuickPoint.",
	fullWelcomeInstructions: "This tour shows you how to create accessible presentations by typing. Use arrow keys or click to navigate.",
	fullRevisitGuide: "Click Tour anytime to revisit this guide.",
	origWelcomeInstructions: "This is a short, simple tour. Use the arrow keys to move through it.",
	origRevisitGuide: "Need to revisit it later? Just click the Tour icon to return here anytime.",

	// contents
	contentsInstructions: "You can navigate directly to any section in the list. If you're new here, we recommend starting from the beginning.",
	contentsAccessibilityTour: "Interested in what makes QuickPoint accessible?",
	contentsAccessibilityTourButton: "Start the Accessibility Tour",
	contentsGettingStarted: "Getting Started",
	contentsStories: "Presentations",
	contentsFormattingText: "Formatting Text",
	contentsLinksAndBacklinks: "Links and Backlinks",
	contentsAddingImages: "Adding Images and Media",
	contentsCharts: "Charts from Data",
	contentsSharingAndPublishing: "Sharing and Publishing",
	contentsMasteringLayouts: "Mastering Layouts",
	contentsMasteringFx: "Mastering Special FX",

	// basics
	basicsTitle: "Getting Started",
	basicsWelcomeAccessibility: "Welcome. QuickPoint turns your words into slides instantly. It is built to be fully accessible* — both for the author and for the viewer.",
	basicsWatchViewer: "Watch the view on the left as we type.",
	basicsAccessibilityFootnote: "*Visit our Accessibility Tour from the main Tour menu to see the complete accessibility features and functions.",
	basicsSimpleIdea: "QuickPoint is built on a simple idea - to use simple language and commands to design and create.",
	basicsKeywordTitle: "creates a title.",
	basicsKeywordImage: "adds an image.",
	basicsKeywordNewPage: "creates a new page.",
	basicsEditorIntro: "This is where you type.",
	basicsViewerIntro: "And this is where your ideas take shape.",
	basicsSimplePrompt: "Let's see a simple prompt in action, starting with a title.",
	basicsYouJustType: "You just type. We handle the design.",
	basicsWordsAppearInstantly: "Your words appear instantly.",
	basicsAddLabel: "Want to make it big? Just add a label. Watch us turn this into a Title.",
	basicsItsATitle: "It's now a Title.",
	basicsChangeColor: "Want to change the color? Just describe it.",
	basicsTypeTitleSuffix: "and QuickPoint knows to create the heading.",
	basicsWordsAppearAsTitle: "The words inputted now appear as a title.",
	basicsTitleInRed: "Let's say we want the title in red - we can describe that as well.",
	basicsIndentAndColor: "Indent the line and type",
	basicsToSeeOptions: "to see options.",
	basicsTypeTabColor: "Type TAB (or two spaces) and then type",
	basicsTypeTabColorSuffix: "and type or select your color of choice.",
	basicsIndentationExplainer: "The indentation with TAB (or two spaces) is necessary for QuickPoint to understand that this is a modifier.",
	basicsMultipleModifiers: "You can add as many modifiers as you like to change the appearance of the element above.",
	basicsTitleAppearsRed: "The title will now appear as red in the left-side viewer.",
	basicsTypePrefix: "Type",
	basicsTypeNewPage: "to start a new page.",
	basicsTypeNewPageOrig: "to create a new page",
	basicsThisIsThe: "This is the",
	basicsVocabBarIntro: ". It shows the keywords you can use at this point in your document.",
	basicsVocabBarUsage: "Click any keyword to insert it into your editor. The bar updates as you type, showing relevant modifiers for the current element.",
	basicsHintsIntro: "For reference, QuickPoint prompts appear here as you're writing.",
	basicsHintsLiveUpdate: "This list updates live, making relevant suggestions in the moment, without getting in the way.",
	basicsChangeMoodPrefix: "Change the mood instantly. Type",
	basicsChangeMoodSuffix: "to style your entire presentation at once.",
	basicsFilterIntroPrefix: "From formal to playful— type",
	basicsFilterIntroSuffix: "to style your piece in one step.",
	basicsDifferentFilters: "Different filters for different needs.",
	basicsHighContrast: "High contrast or soft tones.",
	basicsCoreIdea: "The more you learn, the quicker and more expressive you'll become— but the core idea stays the same: describe what you want, simply.",
	basicsCoveredBasics: "We've covered the basics. You can jump in now, or keep going with the tour.",

	// formatting
	formattingTitle: "Formatting Text",
	formattingIntro: "Let's see how we can format text as bold, underlined, and in italics.",
	formattingBoldPrefix: "To make text bold, surround it with",
	formattingUnderlineMiddle: "to underline or type",
	formattingItalicSuffix: "to italicize text.",
	formattingVoila: "Voilà!",

	// images
	imagesTitle: "Images",
	imagesTitleAccessibility: "Images & Accessibility",
	imagesStoredUnderFiles: "Images are stored under Files.",
	imagesUploadedUnderFiles: "Images are uploaded and stored under Files.",
	imagesUploadAndName: "Upload and name your images. You can add alt-text for accessibility.",
	imagesAltTextDescription: "Images are more than decoration. When you upload an image, QuickPoint prompts you for a description (Alt-text). This ensures every student, including those using screen readers, understands your full presentation.",
	imagesAddImagePrefix: "To add an image, type",
	imagesAddImageSuffix: "and select which image you want to insert.",
	imagesMultiple: "You can add multiple images to a presentation.",
	chartsTitle: "Charts from your data",
	chartsUploadedUnderFiles: "Spreadsheets live under Files too — CSV or Excel, in the Data tab.",
	chartsDataTab: "Drop a CSV or Excel file into the Data tab. Excel converts automatically, and every file needs a name and a description.",
	chartsAddChartPrefix: "Add a chart with",
	chartsAddChartSuffix: "— autocomplete lists your data files.",
	chartsRendered: "The first column becomes the labels and every numeric column a series, drawn in your theme's colours. No chart builder required.",
	imagesMultipleAccessibility: "You can add multiple images to a presentation. Each one needs its own alt-text so all students can follow along.",

	// stories
	storiesTitle: "Presentations",
	storiesWeCallStories: "At QuickPoint, we call your creations \u201Cpresentations.\u201D",
	storiesClickToOpen: "Click here to open your presentations.",
	storiesClickToOpenOrig: "Click here to open. ",
	storiesViewAll: "View all your presentations in one place. Folders keep them organised.",
	storiesViewAllOrig: "View all your presentations in one place.",
	storiesClickToEdit: "Click a presentation to open it for editing.",
	storiesClickToEditOrig: "Click here to open a presentation for editing.",
	storiesCreateOrClose: "Create a new presentation or close this window.",
	storiesFindHelp: "Find QuickPoint help files here.",
	storiesOpenHelp: "Click to open the help folder.",
	storiesLayoutExamples: "See layout and filter examples here.",
	storiesOpenFilters: "Click to open the filters folder.",
	storiesRename: "Click here to rename a presentation.",
	storiesDelete: "Click here to delete a presentation.",

	// grid
	gridTitle: "Complex Layouts.",
	gridIntro: "Let's discover how to create grids.",
	gridTypingSuffix: "will create a grid.",
	gridAddImage: "Then, say, we add an image.",
	gridAdjacentCells: "Items we add on the next line, will create and occupy adjacent cells.",
	gridRowsByDefault: "Grids organize content in rows by default.",
	gridAddRowPrefix: "To add another row, type",
	gridAddRowSuffix: "again.",
	gridAddMoreContent: "We can add more content as we see fit.",
	gridEndGridPrefix: "To end the grid, we simply type",
	gridEndGridSuffix: "again.",
	gridConsiderGrid: "Let's consider creating this grid.",
	gridPipeColumns: "to place content in columns when you're inside a grid.",
	gridColumnNotRows: "This will put what follows in a column rather than rows.",

	// split
	splitTitle: "Layouts",
	splitRearrangeImages: "Let's rearrange the images in our Presentation",
	splitTypePipe: "character to split the page in two and place the images side by side.",
	splitPipeLeftRight: "character splits the page left and right.",
	splitSetBackground: "Let's try setting a background on each side of the split.",
	splitTypeBackground: "and then select the color or image.",

	// links
	linksTitle: "Links And Backlinks",
	linksAddLink: "You can add a link to text by typing {{ and }} around the words you want to be a link.",
	linksUrlPrefix: "When you add the modifier",
	linksUrlSuffix: "and a web address the link is created.",
	linksBacklinkPrefix: "You can also backlink to your own presentations. A list of all your presentations appears when you type",

	// fx
	fxTitle: "Special Effects",
	fxHardToRead: "Consider the following QuickPoint Presentation. The text is hard to read.",
	fxShadowSuffix: "to modify the position of the text and make it easier to read.",
	fxAnimateSuffix: "between words to animate.",
	fxEndAnimation: "End the animation with",
	fxClickPlay: "If you'd like to see it action click the play button.",

	// publishing
	publishingTitle: "Sharing and Publishing",
	publishingSaveAndPublish: "Once you save your presentation, you can publish it to make it immediately shareable.",
	publishingShareWithLink: "QuickPoint presentations are easily shared with a link or a QR code.",
	publishingPdfExport: "Export your presentation as a fully accessible PDF — tagged, structured, and screen-reader friendly.",

	// goodbye
	goodbyeMessage: "That's all for now!",

	// ── Teacher tour ──────────────────────────────────────────────────
	teacherWelcomeIntro: "Welcome to QuickPoint! In the next 2 minutes, you'll create your first presentation.",
	teacherWelcomeNoCoding: "No coding required—just type what you want.",
	teacherWelcomeAccessibility: "QuickPoint is built for accessibility. Author entirely with your keyboard—no mouse needed.",
	teacherWelcomeWcag: "Everything you create is WCAG compliant.",
	teacherWelcomeScreenReaders: "Your presentations work with screen readers, and you'll add alt-text to images so all students can participate.",
	teacherWhereYouType: "This is where you type. Let's make a title.",
	teacherTypeTitleSuffix: "followed by your words. That's it.",
	teacherTitleAppears: "Your title appears instantly on the left.",
	teacherFirstSlide: "You just made your first slide!",
	teacherAddParagraph: "Add any text below and it becomes a paragraph.",
	teacherClickFiles: "Click Files to upload images.",
	teacherUploadDescription: "Upload an image here. Add a description for accessibility—screen readers will read it aloud to students who can't see the image.",
	teacherTypeImageSuffix: "and select your image. Done.",
	teacherClickPublish: "Click Publish to make it shareable.",
	teacherShareViaLink: "Share via link or QR code. Students can view on any device.",
	teacherBasics: "That's the basics! Click the Tour icon anytime to learn more—like adding pages, layouts, and animations.",
	teacherExperiment: "Go ahead and experiment. You can't break anything.",
	teacherHaveFun: "Have fun!",

	// ── A11y tour ─────────────────────────────────────────────────────
	a11yHeading: "Accessibility in QuickPoint",
	a11yIntro: "QuickPoint is built with accessibility at its core. This tour highlights the features that make presentation creation inclusive for everyone.",
	a11yTextBasedHeading: "1. Text-Based, Not Canvas",
	a11yAiNativePrefix: "QuickPoint uses",
	a11yAiNative: "AI-native text creation",
	a11yAiNativeSuffix: "instead of drag-and-drop canvases.",
	a11yChatTab: "Describe what you want in the Chat tab and AI builds it for you. No mouse precision required.",
	a11yStructureTab: "The Structure tab gives full control through plain text.",
	a11yReadableText: "Every presentation is readable, editable text - not locked in a visual canvas. This means screen readers can access all content natively.",
	a11yEditorFull: "The editor has full accessibility support:",
	a11yAriaLive: "Screen reader announcements via aria-live",
	a11yLineByLine: "Line-by-line navigation",
	a11yAutocomplete: "Autocomplete hints accessible by keyboard",
	a11yKeyboardNavHeading: "2. Full Keyboard Navigation",
	a11yAriaTabs: "The editing mode tabs use proper ARIA tab patterns.",
	a11yArrowKeys: "Arrow keys \u2014 move between tabs",
	a11yEnterSpace: "Enter/Space \u2014 activates the selected tab",
	a11yAriaSelected: "aria-selected \u2014 tracks the active tab for screen readers",
	a11yShortcutsHeading: "3. Keyboard Shortcuts",
	a11yShortcutsIntro: "Text formatting works with standard shortcuts:",
	a11yBold: "Cmd/Ctrl + B \u2014 Bold",
	a11yItalic: "Cmd/Ctrl + I \u2014 Italic",
	a11yUnderline: "Cmd/Ctrl + U \u2014 Underline",
	a11yNoClicking: "These work from the keyboard without clicking.",
	a11ySemanticHeading: "4. Semantic Structure",
	a11ySemanticBody: "The app is built from semantic HTML5 landmarks \u2014 header, navigation, main content, and labelled regions \u2014 so screen reader users can jump straight between areas instead of reading through everything.",
	a11ySkipHeading: "5. Skip-to-Content Link",
	a11ySkipLink: "A hidden link appears on first Tab press, letting keyboard users skip past navigation and jump straight to the editor.",
	a11yTryTab: "Try pressing Tab from the top of the page to reveal it.",
	a11yFocusHeading: "6. Focus Visibility and Motion",
	a11yFocusIndicators: "Focus Indicators",
	a11yFocusDesc: "Every interactive element shows a visible blue outline when navigating with the keyboard.",
	a11yReducedMotionHeading: "Reduced Motion",
	a11yReducedMotion: "When your OS is set to Prefer Reduced Motion, animations and transitions are disabled.",
	a11yHighContrastHeading: "High Contrast Mode",
	a11yHighContrast: "Users who prefer increased contrast get enhanced visual distinction in the editor.",
	a11yAutoAdapt: "The system automatically adapts to your OS accessibility preferences.",
	a11yScreenReaderHeading: "7. Screen Reader Support",
	a11yChatAnnounce: "Screen readers automatically announce new chat messages as they arrive, without interrupting the current reading context.",
	a11yEditorAnnounce: "Actions like autocomplete, cursor movement, and text changes are communicated to screen readers through announcements.",
	a11yImageHeading: "8. Image Accessibility",
	a11yAltTextHeading: "Required Alt Text",
	a11yAltTextPrefix: "When uploading images, alt text is a",
	a11yAltTextRequired: "required field",
	a11yAltTextSuffix: ". The upload dialog won't proceed without it.",
	a11yAltTextWhy: "This ensures every image in the presentation has a description for screen reader users.",
	// ── Viewer accessibility ─────────────────────────────────────────
	a11yViewerHeading: "9. Viewer Accessibility Settings",
	a11yViewerIntro: "When your audience views a presentation, they have access to a full suite of accessibility settings.",
	a11yViewerColorsHeading: "Colors & Contrast",
	a11yViewerColors: "Viewers can switch to high-contrast mode or choose custom text and background colors. Several preset themes are available.",
	a11yViewerReadabilityHeading: "Readability",
	a11yViewerNdrFont: "A neuro-divergent-friendly font can be toggled on for easier reading.",
	a11yViewerRuler: "A reading ruler or scrim can be enabled to help readers focus on one section at a time.",
	a11yViewerTint: "A reading tint or overlay can be applied — with preset options like sepia, soft blue, mint green, and Irlen-style filters — or a fully custom color and opacity.",
	a11yViewerTextSizeHeading: "Text Size",
	a11yViewerTextSize: "A font size multiplier (1x–4x) lets viewers enlarge all text to suit their needs.",
	a11yViewerDisplayHeading: "Display Options",
	a11yViewerDisplay: "Viewers can show page numbers and view the source code of any presentation.",
	a11yViewerPdf: "Presentations can also be exported as PDF for offline reading or printing.",

	a11ySummaryHeading: "Accessibility Summary",
	a11ySummaryPrefix: "The authoring tool is designed to meet",
	a11ySummaryStandard: "WCAG 2.2 AA",
	a11ySummarySuffix: "standards:",
	a11ySummaryTextBased: "Text-based authoring (no canvas)",
	a11ySummaryKeyboard: "Full keyboard navigation with ARIA patterns",
	a11ySummaryLandmarks: "Semantic HTML5 landmarks",
	a11ySummarySkipLink: "Skip-to-content link",
	a11ySummaryFocus: "Focus indicators on all controls",
	a11ySummaryLiveRegions: "Screen reader live regions",
	a11ySummaryAltText: "Required alt text on images",
	a11ySummaryMotionContrast: "Reduced motion and high contrast support",
	a11ySummaryContrast: "AAA-level text contrast palette",
	a11ySummaryTargets: "Comfortable 40px+ click and touch targets",
	a11ySummaryViewerSettings: "Viewer accessibility settings (colors, fonts, reading aids, text size)",
	a11yRevisit: "Click Tour anytime to revisit this guide or explore other tours.",
	a11yEnd: "That's the end of the accessibility tour.",
	a11yStartCreating: "Start creating by typing in the Chat tab.",

	// ── Shared typed content for tours ──────────────────────────────
	teacherFirstTitle: "My First Presentation",
	teacherFirstParagraph: "Welcome to my presentation.",
	basicsHelloClass: "Hello Class",
	basicsPage2Title: "Page 2",
	basicsPage2Content: "Where we discuss the implications and effects of words.",
	basicsSomeWords: "Some Words",
	basicsYourWords: "Your Words",
	basicsPowerfullyExpressive: "Powerfully Expressive",
	basicsEssentialCivilisation: "Essential to civilisation",
	basicsFiltersTitle: "Filters.",
	basicsCanMakeIdeas: "Can Make Ideas.",
	basicsFeelDifferent: "Feel different",
	basicsFiltersParagraph: "Than they felt before. We think you'll enjoy browsing through our filters until you find one that filters your idea perfectly.",
	basicsSupertitle: "A Supertitle.",
	basicsThisIsTitle: "This is a Title",
	basicsThisIsSmaller: "This is smaller",
	basicsSomeParagraph: "This is some paragraph text",
	basicsCivilizationQuote: "Civilization began the first time an angry person cast a word instead of a rock.",
	basicsSigmundFreud: "Sigmund Freud",
	bauhausDescription: "The Staatliches Bauhaus, commonly known as the Bauhaus (German for 'building house'), was a German art school operational from 1919 to 1933 that combined crafts and the fine arts.",
	bauhausDescriptionShort: "The Staatliches Bauhaus, commonly known as the Bauhaus (German for 'building house'), was a German art school operational from 1919 to 1933.",
	bauhausInteriorCaption: "A Bauhaus Interior",
	gridCellOne: "Cell One",
	gridCellTwo: "Cell Two",
	gridCellThree: "Cell Three",
	gridCellFour: "Cell Four",
	gridTypingPrefix: "Typing",
	gridYouType: "You type",
};

const stacksEn = {
	openOrCreate: "Open a Presentation Or Create a New One",
	edit: "Edit",
	delete: "Delete",
	stories: "Presentations",
	updated: "Updated",
	created: "Created",
	searchStories: "Search presentations...",
	storyName: "Presentation's name?",
	storyExists: "This presentation already exists. Please choose a different name.",
	pleaseLogin: "Please login first",
	createError: "There was a problem creating a new presentation.",
	enterName: "Please enter a presentation name.",
	nameTooShort: "Name is too short (minimum 3 characters).",
	nameTooLong: "Name is too long (maximum 77 characters).",
	invalidChars: "Only letters, numbers, dashes and underscores are allowed.",
	noSpaces: "Spaces can't be used; use dashes instead.",
	reservedWords: "Words like PAGE and READ can't be used in the name.",
	needNameAfterFolder: "Add a presentation name after the folder path.",
	nameTooShortShort: "Name is too short",
	tryRemovingSpecialChars: "Try removing any special characters or spaces",
	nameTooLongShort: "Name is too long",
};

const authEn = {
	// common
	back: "⬅ Back",
	termsOfService: "Terms Of Service",

	// landing
	login: "Login",
	signUp: "SignUp",
	tour: "Tour",
	landscapeAlert: "Please set your device to landscape and reload.",

	// login choices
	continueGoogle: "Continue with Google",
	continueGithub: "Continue with Github",
	continueEmail: "Continue with Email",

	// login with email
	emailLabel: "Email:",
	emailPlaceholder: "email",
	emailRequired: "Email is required",
	emailInvalid: "A valid Email is required",
	passwordLabel: "Password:",
	passwordPlaceholder: "password",
	passwordRequired: "Password is required",
	enterEmailAlert: "Please enter your account email",
	genericError: "There was a problem. Please try again",
	resetPassword: "Reset Password",
	passwordResetSent: "Password Request Sent. Check your email.",

	// register
	pickNamePrompt: "Please pick a unique name for your account.",
	nameLabel: "Name:",
	namePlaceholder: "Account Name",
	nameTooShort: "Name is too short",
	nameTooLong: "Name is too long",
	nameInvalidChars: "Spaces or special characters are not allowed.",
	checkingName: "Please wait while we see if that name is available",
	nameAvailable: "The name is available. Creating your account",
	nameFree: "This name is available",
	accountReady: "Your account is ready",
	nameTaken: "This name is already taken. Please choose another.",
	registerError: "There was an error. Please refresh the browser and try again",
	storiesUrlPrefix: "All your presentations will be viewable at http://quickpoint/",
	register: "Register",

	// sign up with email
	signUpEmailPlaceholder: "Email",
	signUpPasswordPlaceholder: "Password: 8 letters or more",
	passwordTooShort: "Password must be 8 characters or more.",
	signUpError: "There was an error. Please try again.",
};

const vocabBarEn = {
	barLabel: "Insert keyword",

	// control chars
	newPage: "New page",
	gridRow: "Grid row",
	splitPage: "Split page",

	// keyword help
	"bullet": "Make a bullet point.",
	"indentBullet": "Indent a bullet point.",
	"numberedList": "Make a numbered list.",
	"indentNumbered": "Indent a numbered list item.",
	"audio": "Add an audio file.",
	"autoplay": "Have your presentation play automatically.",
	"autospace": "Toggles automatic vertical spacing between elements.",
	"background": "Change the background to a color or an image.",
	"blank": "Add some empty space.",
	"box": "Highlight text in a stylish box.",
	"button": "Make a clickable button.",
	"chart": "Create a chart from your data.",
	"columnTitle": "Defines a title for a grid column, enabling responsive behavior on mobile.",
	"filter": "Apply a complete visual style to your page.",
	"form": "Ask your audience a question.",
	"image": "Add an image from your files.",
	"line": "Add a decorative line to break up your page.",
	"map": "Add a Google Map.",
	"moveBy": "Choose how to move through your presentation.",
	"paragraph": "Add a paragraph of text.",
	"read": "Add text for 'Read mode'.",
	"storyName": "Give your presentation a name.",
	"subtitle": "Add a subtitle.",
	"supertitle": "Add a big, bold title.",
	"pause": "Pause the presentation.",
	"title": "Add a title.",
	"video": "Add a video from YouTube or Vimeo.",

	// modifier help
	"modAlign": "Align your content.",
	"modAcross": "Place something horizontally.",
	"modAnchor": "Anchor your item.",
	"modAnimate": "Animate an item.",
	"modAnimateStart": "Time your animations.",
	"modBackground": "Add a background color or image.",
	"modBacklink": "Link to another presentation.",
	"modBorderColor": "Choose a border color.",
	"modBorderThickness": "Change the border thickness.",
	"modBlur": "Blur an item.",
	"modCaption": "Add a caption.",
	"modChartType": "Choose a chart type.",
	"modColor": "Choose a color from the palette.",
	"modColumns": "Split text into columns.",
	"modCornerRadius": "Round the corners.",
	"modDecoration": "Decorate your text.",
	"modDelay": "Set a delay for autoplay.",
	"modDown": "Place something vertically.",
	"modEffect": "Add a special text effect.",
	"modFont": "Choose a font.",
	"modHideOn": "Hide an item on desktop or mobile.",
	"modLoop": "Loop your auto playing presentation.",
	"modToPage": "Link to another page.",
	"modHeight": "Change the height.",
	"modLineHeight": "Change the line height.",
	"modMailto": "Create an email link.",
	"modMute": "Mute a video.",
	"modOpacity": "Change the transparency.",
	"modPadding": "Add space around an item.",
	"modPaddingBottom": "Add space below an item.",
	"modPaddingLeft": "Add space to the left of an item.",
	"modPaddingRight": "Add space to the right of an item.",
	"modPaddingTop": "Add space above an item.",
	"modSize": "Choose a size.",
	"modShadow": "Add a shadow.",
	"modUrl": "Link to a website.",
	"modWeight": "Change the font weight.",
	"modWidescreen": "Make your video widescreen.",
	"modWidth": "Change the width.",
};

// ── `text` — translatable user-facing strings ────────────────────────

export const text = {
	controls: localized("controls", controlsEn),
	textControls: localized("textControls", textControlsEn),
	errors: localized("errors", errorsEn),
	lint: {
		actions: localizedNested("lint", "actions", lintActionsEn),
		messages: localizedNestedMixed("lint", "messages", lintMessagesEn),
	},
	ui: localizedMixed("ui", uiEn),
	preview: localizedMixed("preview", previewEn),
	reader: localizedMixed("reader", readerEn),
	chat: localized("chat", chatEn),
	// mixed: balance/purchased are functions
	tkns: localizedMixed("tkns", tknsEn),
	// mixed: jumpToSectionLabel is a function string
	tour: localizedMixed("tour", tourEn),
	tourSteps: localizedMixed("tourSteps", tourStepsEn),
	share: localizedMixed("share", shareEn),
	stacks: localized("stacks", stacksEn),
	auth: localized("auth", authEn),
	vocabBar: localized("vocabBar", vocabBarEn),
	fileUploader: localizedMixed("fileUploader", fileUploaderEn),
	imagePicker: localizedMixed("imagePicker", imagePickerEn),
};

// ── `strings` — non-translatable internal constants ──────────────────

export const strings = {
	errors: {
		noStack: errorsEn.noStack,
		notFound: errorsEn.notFound,
	},
	broadcastChannel: {
		allAuthors: "allAuthors",
		author: "quickpoint_channel_author",
		fileComponent: "quickpoint_file_component",
		fromUploader: "quickpoint_from_uploader",
		general: "general",
		preview: "quickpoint_channel_preview",
		toUploader: "quickpoint_to_uploader",
		viewer: "quickpoint_channel_viewer",
	},
	common: {
		unauthorized: "unauthorized",
		landing: { qp: "quickpoint/landing", m23: "mrkt23/landing", tkn: "tkns/landing" }
	},
	demo: {
		cfKey: "demo/demo",
		ownerName: "demo"
	},
	controlsPortalId: {
		files: "files-control-button-portal",
		logout: "logout-control-button-portal",
		mobilePreview: "mobile-preview-button-portal",
		newStack: "new-stack-control-button-portal",
		preview: "preview-control-button-portal",
		publish: "publish-control-button-portal",
		qr: "qr-control-button-portal",
		read: "read-control-button-portal",
		save: "save-control-button-portal",
		stacks: "stacks-control-button-portal",
	},
	context: {
		files: "files",
		filter: "filter",
		hints: "hints",
		init: "init",
		stacks: "stacks",
		topLevel: "topLevel",
	},
	css: {
		authorUserInterface: "author-user-interface",
		authorUserInterfaceClass: "author-user-interface",
		displayGrid: "quickpoint-display-grid",
		displayGridClass: ".quickpoint-display-grid",
		dynamicResize: "dynamic-resize",
		dynamicResizeClass: ".dynamic-resize",
		fileDropZone: "file-drop-zone",
		grid: "quickpoint-grid",
		gridClass: ".quickpoint-grid",
		image: "image",
		imageClass: ".image",
		landscapePreviewGeneratorID: "landscape-preview-generator",
		landscapeViewerID: "landscape-viewer",
		pageElement: "page-element",
		pageElementClass: ".page-element",
		portraitPreviewGeneratorID: "portrait-preview-generator",
		portraitViewerID: "portrait-viewer",
		preventDragDropDefault: "prevent-drag-drop-default",
		previewGenerator: "preview-generator",
		previewPane: "preview-pane",
		previewPaneClass: ".preview-pane",
		safariWrapper: "safari-wrapper",
		splitPage: "split-page",
		stack: "stack",
		stickyHeader: "sticky-menu",
		styledGridCell: "styled-grid-cell",
		styledGridCellClass: ".styled-grid-cell",
		viewerContainer: "viewer-container",
		viewerContainerClass: ".viewer-container",
	},
	defaultFilter: "quickpoint::default",
	events: {
		aiStreamStart: "aiStreamStart",
		aiStreamChunk: "aiStreamChunk",
		aiStreamEnd: "aiStreamEnd",
		clearCode: "clearCode",
		fold: "foldCode",
		insertCharacter: "insertCharacter",
		insertWord: "insertWord",
		openAutoComplete: "openAutoComplete",
		moveCursorToPageIndex: "moveCursorToPageIndex",
		undo: "undo",
		updateAppVars: "updateAppVars",
		updateAudioFiles: "updateAudioFiles",
		updateBacklinks: "updateBacklinks",
		updateCode: "updateCode",
		updateDataFiles: "updateDataFiles",
		updateFilterColors: "updateFilterColors",
		updateFilterNames: "updateFilterNames",
		updateImageFiles: "updateImageFiles",
		updateLoadedFilterFonts: "updateLoadedFilterFonts",
		updateStackCfKeys: "updateStackCfKeys",
		wrapSelection: "wrapSelection",
	},
	localStorage: {
		ownerName: "ownerName",
		authObject: "authObject",
		ownerId: "ownerId",
		QpHistoryLocalStorage: "QpHistoryLocalStorage",
		QpHistorySessionStorage: "QpHistorySessionStorage",
	},
	messages: {
		allFiles: "allFiles",
		applicationError: "applicationError",
		authorIsReady: "authorIsReady",
		deleteImage: "deleteImage",
		deleteImageFailed: "deleteImageFailed",
		deleteStack: "deleteStack",
		edit: "edit",
		elementClick: "elementClick",
		exitPresentationMode: "exitPresentationMode",
		enterReadMode: "enterReadMode",
		exitReadMode: "exitReadMode",
		error: "error",
		fileUpload: "fileUpload",
		filtersAreLoaded: "filtersAreLoaded",
		goToPage: "goToPage",
		init: "init",
		injectFilter: "injectFilter",
		invalidEmail: "invalidEmail",
		linkClicked: "linkClicked",
		orgNameRequired: "orgNameRequired",
		page: "page",
		parsedStack: "parsedStack",
		ping: "ping",
		pong: "pong",
		preview: "preview",
		previewSnapDone: "previewSnapDone",
		presentationMode: "presentationMode",
		ready: "ready",
		restartPreview: "restartPreview",
		requestAllFiles: "requestAllFiles",
		requestFilterNames: "requestFilterNames",
		requestPreviewSnap: "requestPreviewSnap",
		requestStack: "requestStack",
		saveStack: "saveStack",
		setFilterColors: "setFilterColors",
		setFilterFonts: "setFilterFonts",
		setFilterNames: "setFilterNames",
		setNavigationType: "setNavigationType",
		setStoryNames: "setStoryNames",
		toggleStoriesDuringDemoMode: "toggleStoriesDuringDemoMode",
		test: "test",
		toggleFilesPanel: "toggleFilesPanel",
		toggleSharePanel: "toggleSharePanel",
		toggleStacksPanel: "toggleStacksPanel",
		unPreview: "unPreview",
		updateFiles: "updateFiles",
		weakPassword: "weakPassword",
		getCodeAndFilter: "getCodeAndFilter",
		codeAndFilter: "codeAndFilter",
		code: "code",
	},
	modifier: "modifier",
	navigation: {
		idStoryBackgroundLeft: "#presentation-background-left",
		idStoryBackgroundRight: "#presentation-background-right",
		idStoryBackgroundTop: "#presentation-background-top",
		idStoryBackgroundBottom: "#presentation-background-bottom",
		storyBackgroundLeft: "presentation-background-left",
		storyBackgroundRight: "presentation-background-right",
		storyBackgroundTop: "presentation-background-top",
		storyBackgroundBottom: "presentation-background-bottom",
	},
	preview: {
		inMobile: "previewInMobile",
		inDesktop: "previewInDesktop",
		inExternalMobile: "previewInExternalMobile",
	},
	reservedWordsArray: ["READ", "PAGE"],
	reservedWords: {
		read: "READ",
		page: "PAGE",
		debug: "DEBUG",
	},
	signUpMethods: {
		google: "google",
		github: "github",
		email: "email",
	},
	startOfLine: "startOfLine",
	startOfLinePlusTab: "startOfLinePlusTab",
	textControlsPortalId: {
		bold: "bold-text-control-button-portal",
		col: "col-text-control-button-portal",
		page: "page-text-control-button-portal",
		fold: "fold-text-control-button-portal",
		italic: "italic-text-control-button-portal",
		row: "row-text-control-button-portal",
		underline: "underline-text-control-button-portal",
	},
	tests: {
		failMail: "fail@fail.com",
		gitHub: "github@test.com",
		gmail: "google@test.com",
		jwt: "testJwt",
		integrationTestImage: "integrationTestImage",
		integrationTestImageURL:
			"https://imagedelivery.net/KVUPIyykyRSaaOckEeUn5g/6907ee2e-9318-4ca5-6737-281f2237ee00/public",
		noNameMail: "noname@test.com",
		testMail: "greg@test.test",
		testPassword: "testing",
		url: "/testOwner/testStack",
	},
	ui: {
		portrait: "portrait",
		landscape: "landscape",
	},
	unset: "unset",
	url: {
		helpUrl: "help/",
		page: "PAGE",
		read: "READ",
		showLogin: "login",
		showSignUp: "signup",
		userRequestsGitHubSignUp: "github-signup",
		userRequestsGoogleSignUp: "google-signup",
		userRequestsLoginWithEmail: "email-login",
		userRequestsSignUpWithEmail: "email-signup",
	},
	utility: {
		alreadyAuth: "alreadyAuth",
		author: "author",
		badUrl: "badUrl",
		contentUrl: "internal-content-link",
		defaultFilter: "quickpoint::default",
		emailLinkExpired: "emailLinkExpired",
		inAuthor: "inAuthor",
		invalidCredentials: "invalidCredentials",
		loginError: "loginError",
		networkError: "networkError",
		postLogin: "postLogin",
		signedUp: "signedUp",
		signUpTime: "signUpTime",
		sourceCode: "sourceCode",
		unverifiedEmail: "unverifiedEmail",
		viewerati: "viewerati",
		loading: "loading",
		noOwnerNameAssociatedWithEmail: "noOwnerNameAssociatedWithEmail",
		noRecordFound: "noVespaRecordFound",
	},
	workers: {
		datafilesR2: "datafiles",
		missingFields: "missing fields",
		ownerExists: "owner exists",
		reserved: "reserved",
		turnStileError: "turnstile error",
		userExists: "user exists",
	},
} as const;
